import { MDBCol, MDBContainer, MDBRow, MDBTypography, MDBSwitch } from "mdb-react-ui-kit";
import styled from "styled-components";
import { useAuth } from "../../../components/Auth";
import { SignalThresholdPopover } from "stm-frontend-library";

export const StyledSwitchWrapper = styled.div`
  label.form-check-label {
    color: #212529 !important;
    opacity: 1 !important;
  }

  .form-check-input:disabled ~ .form-check-label:hover {
    color: #212529 !important;
  }
`;

const DisabledStyledSwitch = (props) => (
  <StyledSwitchWrapper>
    <MDBSwitch {...props} disabled />
  </StyledSwitchWrapper>
);

const SignalRow = ({ label, checked, toolKey, signalKey = null, toolConfig = null }) => (
  <div className="d-flex align-items-center">
    <DisabledStyledSwitch label={label} checked={checked} />
    {checked && (
      <SignalThresholdPopover
        toolKey={toolKey}
        signalKey={signalKey}
        label={label}
        toolConfig={toolConfig}
      />
    )}
  </div>
);

export const SwitchGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.gap || "0.7rem"};
`;

const FeatureSection = ({
    title,
    visible = true,
    messageWhenHidden = null,
    className = "mb-3",
    children,
}) => {
    return (
        <MDBRow className={className}>
            <hr className="hr" />
            <MDBTypography variant="h6" className="mb-3 fw-bold">
                {title}
            </MDBTypography>
            {visible
                ? children
                : (
                    <MDBTypography className="text-muted fst-italic">
                        {messageWhenHidden}
                    </MDBTypography>
                )}
        </MDBRow>
    );
};

const SignalsTab = () => {
  const { profile, profileRawData, cactusToolConfigExists } = useAuth();
  const tools = profileRawData?.tools ?? {};

  return (
    <MDBContainer className="m-5">
      <MDBTypography variant="h5" className="mb-4 fw-bold">
        Ambient Screening Profile
      </MDBTypography>
      <MDBRow className="mb-3">
        <MDBCol>
          <MDBTypography variant="h6" className="mb-3 fw-bold">
            PubPeer
          </MDBTypography>
          <SwitchGroup className="mb-2">
            <SignalRow
              label="PubPeer hits"
              checked={!!profile["tool.pubPeerHits"]}
              toolKey="pubPeerHits"
              toolConfig={tools.pubPeerHits}
            />
          </SwitchGroup>
        </MDBCol>
        <MDBCol>
          <MDBTypography variant="h6" className="mb-3 fw-bold">
            ClearSkies Papermill Alarm: Public
          </MDBTypography>
          <SwitchGroup className="mb-2">
            <SignalRow
              label="Papermill similarity"
              checked={!!profile["tool.papermillSimilarity"]}
              toolKey="papermillSimilarity"
              toolConfig={tools.papermillSimilarity}
            />
          </SwitchGroup>
        </MDBCol>
      </MDBRow>

      <FeatureSection
        title="Cactus Integrity Checks"
        messageWhenHidden="Cactus Integrity Checks are not available for this publisher"
        visible={cactusToolConfigExists}
      >
        <MDBCol>
          <SwitchGroup>
            <SignalRow
              label="Author affiliation relevance"
              checked={!!profile["tool.cactusTool.authorAffiliationRelevance"]}
              toolKey="cactusTool"
              signalKey="authorAffiliationRelevance"
              toolConfig={tools.cactusTool}
            />
            <SignalRow
              label="Author geographical distribution"
              checked={!!profile["tool.cactusTool.authorGeographicalDistribution"]}
              toolKey="cactusTool"
              signalKey="authorGeographicalDistribution"
              toolConfig={tools.cactusTool}
            />
            <SignalRow
              label="Authors with previous retractions"
              checked={!!profile["tool.cactusTool.authorWithRetraction"]}
              toolKey="cactusTool"
              signalKey="authorWithRetraction"
              toolConfig={tools.cactusTool}
            />
            <SignalRow
              label="Age of reference"
              checked={!!profile["tool.cactusTool.ageOfReference"]}
              toolKey="cactusTool"
              signalKey="ageOfReference"
              toolConfig={tools.cactusTool}
            />
          </SwitchGroup>
        </MDBCol>
        <MDBCol>
          <SwitchGroup>
            <SignalRow
              label="Reference citation"
              checked={!!profile["tool.cactusTool.referenceCitation"]}
              toolKey="cactusTool"
              signalKey="referenceCitation"
              toolConfig={tools.cactusTool}
            />
            <SignalRow
              label="Citation cartel"
              checked={!!profile["tool.cactusTool.citationCartel"]}
              toolKey="cactusTool"
              signalKey="citationCartel"
              toolConfig={tools.cactusTool}
            />
            <SignalRow
              label="Self citation"
              checked={!!profile["tool.cactusTool.selfCitation"]}
              toolKey="cactusTool"
              signalKey="selfCitation"
              toolConfig={tools.cactusTool}
            />
            <SignalRow
              label="Unrelated references"
              checked={!!profile["tool.cactusTool.unrelatedReferences"]}
              toolKey="cactusTool"
              signalKey="unrelatedReferences"
              toolConfig={tools.cactusTool}
            />
          </SwitchGroup>
        </MDBCol>
      </FeatureSection>

      <MDBRow className="mb-3">
        <hr className="hr" />
        <MDBCol>
          <MDBTypography variant="h6" className="mb-3 fw-bold">
            Reference analysis
          </MDBTypography>
          <SwitchGroup>
            <SignalRow
              label="Retraction Watch"
              checked={!!profile["tool.retractionWatch"]}
              toolKey="retractionWatch"
              toolConfig={tools.retractionWatch}
            />
            <SignalRow
              label="DOI analysis"
              checked={!!profile["tool.doiAnalysis"]}
              toolKey="doiAnalysis"
              toolConfig={tools.doiAnalysis}
            />
            <SignalRow
              label="Crossref checks"
              checked={!!profile["tool.crossrefChecks"]}
              toolKey="crossrefChecks"
              toolConfig={tools.crossrefChecks}
            />
            <SignalRow
              label="Context checks"
              checked={!!profile["tool.contextChecks"]}
              toolKey="contextChecks"
              toolConfig={tools.contextChecks}
            />
          </SwitchGroup>
        </MDBCol>
        <MDBCol>
          <MDBTypography variant="h6" className="mb-3 fw-bold">
            Text analysis
          </MDBTypography>
          <SwitchGroup>
            <SignalRow
              label="Tortured phrases"
              checked={!!profile["tool.torturedPhrases"]}
              toolKey="torturedPhrases"
              toolConfig={tools.torturedPhrases}
            />
            <SignalRow
              label="Suspect phrases"
              checked={!!profile["tool.suspectPhrases"]}
              toolKey="suspectPhrases"
              toolConfig={tools.suspectPhrases}
            />
            <SignalRow
              label="GenAI response"
              checked={!!profile["tool.genAIResponse"]}
              toolKey="genAIResponse"
              toolConfig={tools.genAIResponse}
            />
            <SignalRow
              label="Nonsensical text"
              checked={!!profile["tool.geppetto"]}
              toolKey="geppetto"
              toolConfig={tools.geppetto}
            />
          </SwitchGroup>
        </MDBCol>
      </MDBRow>

      <MDBRow className="mb-3">
        <hr className="hr" />
        <MDBCol>
          <MDBTypography variant="h6" className="mb-3 fw-bold">
            Metadata analysis
          </MDBTypography>
          <SignalRow
            label="Word file metadata"
            checked={!!profile["tool.wordFileMetadata"]}
            toolKey="wordFileMetadata"
            toolConfig={tools.wordFileMetadata}
          />
        </MDBCol>
        <MDBCol>
          <MDBTypography variant="h6" className="mb-3 fw-bold">
            Duplicate submissions
          </MDBTypography>
          <SwitchGroup>
            <SignalRow
              label="Metadata screening"
              checked={!!profile["tool.duplicateSubmissions.metadataScreening"]}
              toolKey="duplicateSubmissions"
              signalKey="metadataScreening"
              toolConfig={tools.duplicateSubmissions}
            />
            <SignalRow
              label="Full-text screening"
              checked={!!profile["tool.duplicateSubmissions.fullTextScreening"]}
              toolKey="duplicateSubmissions"
              signalKey="fullTextScreening"
              toolConfig={tools.duplicateSubmissions}
            />
          </SwitchGroup>
        </MDBCol>
      </MDBRow>

      <MDBRow className="mb-3">
        <hr className="hr" />
        <MDBCol>
          <MDBTypography variant="h6" className="mb-3 fw-bold">
            SignalCheck
          </MDBTypography>
          <SwitchGroup>
            <SignalRow
              label="Fake affiliation names"
              checked={!!profile["tool.signalCheck.fakeAffiliationNames"]}
              toolKey="signalCheck"
              signalKey="fakeAffiliationNames"
              toolConfig={tools.signalCheck}
            />
            <SignalRow
              label="Fake names or e-mail addresses"
              checked={!!profile["tool.signalCheck.fakeNamesOrEmailAddresses"]}
              toolKey="signalCheck"
              signalKey="fakeNamesOrEmailAddresses"
              toolConfig={tools.signalCheck}
            />
            <SignalRow
              label="Fake e-mail domains"
              checked={!!profile["tool.signalCheck.fakeEmailDomains"]}
              toolKey="signalCheck"
              signalKey="fakeEmailDomains"
              toolConfig={tools.signalCheck}
            />
            <SignalRow
              label="Disposable e-mail domains"
              checked={!!profile["tool.signalCheck.disposableEmailDomains"]}
              toolKey="signalCheck"
              signalKey="disposableEmailDomains"
              toolConfig={tools.signalCheck}
            />
          </SwitchGroup>
        </MDBCol>
        <MDBCol>
          <SwitchGroup style={{ marginTop: "33px" }}>
            <SignalRow
              label="Suspect articles"
              checked={!!profile["tool.signalCheck.suspectArticles"]}
              toolKey="signalCheck"
              signalKey="suspectArticles"
              toolConfig={tools.signalCheck}
            />
            <SignalRow
              label="Manuscripts offered for sale"
              checked={!!profile["tool.signalCheck.manuscriptsOfferedForSale"]}
              toolKey="signalCheck"
              signalKey="manuscriptsOfferedForSale"
              toolConfig={tools.signalCheck}
            />
            <SignalRow
              label="Metadata suspects"
              checked={!!profile["tool.signalCheck.metadataSuspects"]}
              toolKey="signalCheck"
              signalKey="metadataSuspects"
              toolConfig={tools.signalCheck}
            />
          </SwitchGroup>
        </MDBCol>
      </MDBRow>
    </MDBContainer>
  );
};

export default SignalsTab;
